import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdSearch } from 'react-icons/md';
import {
  ColumnFilterHeader,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { formatCurrency } from './financeData';
import { getGymMemberId } from '../members/memberData';
import {
  saleRowPaid,
  saleRowTotal,
} from '../../receptionist/sales/salePaymentUtils';
import { fetchCustomerBalancesPage } from '../../../services/receptionist/receptionistApi';
import { setActiveGymId } from '../../../services/receptionist/gymContext';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import { useServerPagedList } from '../../../hooks/useServerPagedList';
import TablePagination from '../../../components/shared/TablePagination';
import './finance.css';

function paymentMatchesMember(payment, member) {
  if (!member?.id) return false;
  if (payment.memberId && payment.memberId === member.id) return true;
  const payGym = String(payment.gymId || '').toLowerCase().replace(/\s+/g, '');
  const memGym = String(getGymMemberId(member) || '').toLowerCase().replace(/\s+/g, '');
  if (payGym && memGym && payGym === memGym) return true;
  const payName = String(payment.member || '').trim().toLowerCase();
  const memName = String(member.name || '').trim().toLowerCase();
  return Boolean(payName && memName && payName === memName);
}

/** Aggregate debit (payments), credit (sales), and balance per member.
 *  Only customers with pending balance (> 0). Walk-in only when still due.
 */
export function buildCustomerBalanceRows(members = [], payments = []) {
  return (members || [])
    .map((member) => {
      const memberPayments = (payments || []).filter((p) => paymentMatchesMember(p, member));
      const credit = memberPayments.reduce((sum, row) => sum + saleRowTotal(row), 0);
      const debit = memberPayments.reduce((sum, row) => sum + saleRowPaid(row), 0);
      const balance = Math.round((credit - debit) * 100) / 100;
      const salesCount = memberPayments.length;
      return {
        id: member.id,
        gymId: getGymMemberId(member) || '—',
        name: member.name || '—',
        phone: member.phone || member.mobile || '—',
        branch: member.branch || '—',
        salesCount,
        credit: Math.round(credit * 100) / 100,
        debit: Math.round(debit * 100) / 100,
        balance,
      };
    })
    .filter((row) => row.balance > 0)
    .sort((a, b) => {
      if (b.balance !== a.balance) return b.balance - a.balance;
      return String(a.name).localeCompare(String(b.name));
    });
}

export default function CustomerBalanceList({ gymId }) {
  const [search, setSearch] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    gymId: '',
    name: '',
    phone: '',
    branch: '',
    salesCount: '',
    debit: '',
    credit: '',
    balance: '',
  });

  useEffect(() => {
    if (gymId) setActiveGymId(gymId);
  }, [gymId]);

  const fetchPage = useCallback(async (params) => {
    if (gymId) setActiveGymId(gymId);
    return fetchCustomerBalancesPage(params);
  }, [gymId]);

  const {
    rows: balanceRows,
    total,
    page,
    setPage,
    totalPages,
    from,
    to,
    loading,
    error,
    reload,
    nextPage,
    prevPage,
  } = useServerPagedList({ fetcher: fetchPage, search, filters });

  useEffect(() => {
    const refresh = () => reload();
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [reload]);

  const filteredRows = balanceRows;

  const totals = useMemo(() => filteredRows.reduce(
    (acc, row) => ({
      credit: acc.credit + row.credit,
      debit: acc.debit + row.debit,
      balance: acc.balance + row.balance,
    }),
    { credit: 0, debit: 0, balance: 0 },
  ), [filteredRows]);

  return (
    <div className="admin-customer-balance-list">
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading customer balances…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card admin-finance-section">
        <div className="admin-finance-member-header">
          <h2 className="owner-card-title">Customer Balance List</h2>
          <p className="owner-placeholder-text" style={{ margin: 0 }}>
            {filteredRows.length} customer{filteredRows.length === 1 ? '' : 's'} with pending balance
            {' · '}
            Pending: <strong className="finance-amount-negative">{formatCurrency(totals.balance)}</strong>
          </p>
        </div>

        <div className="admin-customer-balance-toolbar">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search name, serial no, phone, branch…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <p className="owner-placeholder-text admin-customer-balance-hint">
          Only customers with amount still due. Walk-in appears only when it has a pending balance.
          {' '}
          Sale = Credit · Payment = Debit · Balance = Credit − Debit
        </p>

        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-customer-balance-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Customer"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search customer..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search phone..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Sales"
                  filterKey="salesCount"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search sales..."
                />
                <ColumnFilterHeader
                  label="Debit (Payment)"
                  filterKey="debit"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Credit (Sale)"
                  filterKey="credit"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Balance"
                  filterKey="balance"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <th>Ledger</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length ? (
                <>
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td className="rec-member-gym-id">{row.gymId}</td>
                      <td>{row.name}</td>
                      <td>{row.phone}</td>
                      <td>{row.branch}</td>
                      <td>{row.salesCount}</td>
                      <td className={row.debit > 0 ? 'finance-amount-positive' : ''}>
                        {formatCurrency(row.debit)}
                      </td>
                      <td>{formatCurrency(row.credit)}</td>
                      <td className={row.balance > 0 ? 'finance-amount-negative' : 'finance-amount-positive'}>
                        {formatCurrency(row.balance)}
                      </td>
                      <td>
                        <Link
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          to={`/admin/all-module/${row.id}`}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                  <tr className="admin-customer-balance-total-row">
                    <td colSpan={5}><strong>Total</strong></td>
                    <td><strong>{formatCurrency(totals.debit)}</strong></td>
                    <td><strong>{formatCurrency(totals.credit)}</strong></td>
                    <td className={totals.balance > 0 ? 'finance-amount-negative' : 'finance-amount-positive'}>
                      <strong>{formatCurrency(totals.balance)}</strong>
                    </td>
                    <td />
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={9} className="owner-placeholder-text">
                    {loading ? 'Loading…' : 'No customers with pending balance.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>
    </div>
  );
}
